import { useEffect, useMemo, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Camera, X } from "lucide-react";
import { IMAGE_ACCEPT, isImage } from "@/lib/upload";
import { toast } from "sonner";

export function PhotoPicker({ files, setFiles, max = 4 }) {
  const ref = useRef(null);
  const previews = useMemo(() => files.map((f) => URL.createObjectURL(f)), [files]);
  useEffect(() => () => previews.forEach((u) => URL.revokeObjectURL(u)), [previews]);

  const pick = (e) => {
    const chosen = Array.from(e.target.files || []);
    const valid = chosen.filter((f) => isImage(f) && f.size <= 10 * 1024 * 1024);
    if (valid.length !== chosen.length) toast.error("Only JPG, PNG or WEBP photos under 10 MB");
    const next = [...files, ...valid].slice(0, max);
    if (files.length + valid.length > max) toast.error(`You can attach up to ${max} photos`);
    setFiles(next);
    e.target.value = "";
  };

  return (
    <div data-testid="photo-picker">
      <div className="flex flex-wrap gap-2">
        {files.map((f, i) => (
          <div key={`${f.name}-${f.lastModified}-${f.size}`} className="relative h-20 w-20 rounded-lg overflow-hidden border border-slate-200 bg-slate-50" data-testid={`photo-preview-${i}`}>
            <img src={previews[i]} alt="" className="h-full w-full object-cover" />
            <button type="button" onClick={() => setFiles(files.filter((_, idx) => idx !== i))} data-testid={`photo-remove-${i}`}
              className="absolute top-1 right-1 h-5 w-5 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80">
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
        {files.length < max && (
          <button type="button" onClick={() => ref.current?.click()} data-testid="photo-add"
            className="h-20 w-20 rounded-lg border-2 border-dashed border-slate-300 text-slate-400 hover:border-primary hover:text-primary flex flex-col items-center justify-center gap-1 text-[11px] font-semibold transition-colors">
            <Camera className="h-5 w-5" /> Add photo
          </button>
        )}
      </div>
      <input ref={ref} type="file" accept={IMAGE_ACCEPT} multiple className="hidden" onChange={pick} data-testid="photo-input" />
      <p className="text-[11px] text-slate-400 mt-1.5">{files.length}/{max} photos · JPG, PNG or WEBP</p>
      {files.length > 0 && <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs text-slate-500" onClick={() => setFiles([])}>Clear</Button>}
    </div>
  );
}
